import axios from 'axios';

/**
 * Application errors surfaced to users. Server codes come from
 * micus-mono/src/lib/appError.ts; client-only codes are defined below.
 */
export type AppErrorInfo = {
  code: string;
  title: string;
  message: string;
  status?: number;
  requestId?: string;
  redirectUrl?: string;
  details?: { field: string; message: string }[];
};

export const ServerErrorCode = {
  VALIDATION_FAILED: 'MIC-VAL-001',
  CAPTCHA_FAILED: 'MIC-CAP-001',
  SUBMISSION_UNVERIFIED: 'MIC-BOT-001',
  SUBMISSION_EXPIRED: 'MIC-BOT-004',
  LEAD_EMAIL_EXISTS: 'MIC-LEAD-409',
  RATE_LIMITED: 'MIC-RATE-429',
  ROUTE_NOT_FOUND: 'MIC-SYS-404',
  INTERNAL_ERROR: 'MIC-SYS-500',
  SERVICE_UNAVAILABLE: 'MIC-SYS-503',
} as const;

const CLIENT_ERRORS = {
  REQUIRED_FIELDS: { code: 'MIC-VAL-001', title: 'Check your details', message: 'Please fill in all required fields.' },
  INVALID_EMAIL: { code: 'MIC-VAL-002', title: 'Check your email', message: 'Please enter a valid email address.' },
  INVALID_PHONE: { code: 'MIC-VAL-003', title: 'Check your phone', message: 'Please enter a valid phone number, e.g. +91 98765 43210.' },
  CAPTCHA_REQUIRED: { code: 'MIC-CAP-002', title: 'Verification required', message: 'Please answer and verify the security check first.' },
  CAPTCHA_UNAVAILABLE: { code: 'MIC-CAP-003', title: 'Security check unavailable', message: 'We could not load the security check. Please refresh it.' },
  TOO_FAST: { code: 'MIC-BOT-002', title: 'One moment', message: 'Please take a moment to review the form, then submit again.' },
  NO_INTERACTION: { code: 'MIC-BOT-003', title: 'Submission not verified', message: 'Please click or type in the form before submitting.' },
  SECURITY_CHECK_UNAVAILABLE: { code: 'MIC-BOT-005', title: 'Security check unavailable', message: 'We could not complete the background security check. Please check your connection and submit again.' },
  EMAIL_NOT_DELIVERED: { code: 'MIC-MAIL-503', title: 'Workspace created — email not delivered', message: 'We couldn’t send your sign-in details just now. Please submit the form again in a few minutes to resend them.' },
  NETWORK: { code: 'MIC-NET-001', title: 'Connection problem', message: 'We could not reach Micus. Check your connection and try again.' },
  TIMEOUT: { code: 'MIC-NET-408', title: 'Request timed out', message: 'Micus took too long to respond. Please try again.' },
  UNKNOWN: { code: 'MIC-SYS-500', title: 'Something went wrong', message: 'An unexpected error occurred. Please try again.' },
} as const satisfies Record<string, AppErrorInfo>;

export type ClientErrorKey = keyof typeof CLIENT_ERRORS;

export function clientError(key: ClientErrorKey, message?: string): AppErrorInfo {
  const def = CLIENT_ERRORS[key];
  return { ...def, message: message || def.message };
}

type ApiErrorBody = {
  message?: string;
  error?: Partial<AppErrorInfo>;
};

export function toAppError(err: unknown): AppErrorInfo {
  if (!axios.isAxiosError(err)) return clientError('UNKNOWN');
  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') return clientError('TIMEOUT');
  if (!err.response) return clientError('NETWORK');

  const status = err.response.status;
  const body = (err.response.data || {}) as ApiErrorBody;
  const e = body.error;

  if (e?.code) {
    return {
      code: e.code,
      title: e.title || 'Request failed',
      message: e.message || body.message || clientError('UNKNOWN').message,
      status,
      requestId: e.requestId,
      redirectUrl: e.redirectUrl,
      details: e.details,
    };
  }

  return {
    code: `MIC-HTTP-${status}`,
    title: 'Request failed',
    message: body.message || e?.message || `The server responded with status ${status}.`,
    status,
  };
}

const MUSB_URL = (import.meta.env.VITE_MUSB_URL || 'http://localhost:1703').replace(/\/$/, '');

export function musbLoginUrl(email: string): string {
  return `${MUSB_URL}/login?email=${encodeURIComponent(email)}&from=web`;
}
